import { Chain, ChainError, bodyOf, entryHash, parseAnchorMemo, verifyCheckpointSig } from '../core/log.js';
import { verifyHex } from '../core/crypto.js';
import { canonicalize } from '../core/canonical.js';
import type { Checkpoint, LogEntry, SystemEvent } from '../core/types.js';

/**
 * What the verifier needs to read from Zcash. Anyone can implement this with
 * their own node and the vault's published viewing key, so nobody has to
 * trust the operator's word about deposits, withdrawals or anchors.
 */
export interface ChainView {
  /**
   * A credited deposit as seen on-chain, or null if it does not exist.
   * `expectedAddress` is the deposit address the log names; a view that sees
   * the same destination in another encoding returns it as that address.
   */
  getDeposit(ref: string, txid: string, expectedAddress?: string): Promise<{ address: string; zat: bigint; confirmations: number } | null>;
  /**
   * Payment made by the vault in `txid`, or null. With `address`, only an
   * output to that address counts; without it (the log keeps withdrawal
   * addresses sealed), the transaction's one outgoing payment.
   */
  getPayment(txid: string, address?: string): Promise<{ zat: bigint; fee: bigint; confirmations: number } | null>;
  /** Memo of an anchor transaction, or null. */
  getAnchorMemo(txid: string): Promise<{ memo: string; confirmations: number } | null>;
  /** Spendable vault balance (confirmed). */
  getVaultBalance(): Promise<bigint>;
  /** Every memo received by the anchor address in mined transactions (for rebuilding the log from the chain). */
  listAnchorMemos?(): Promise<Uint8Array[]>;
}

export interface VerifyOptions {
  expectSequencer?: string;
  chain?: ChainView;
  /** Receipts users kept. A receipt that differs from the published log proves equivocation. */
  receipts?: LogEntry[];
  minConfirmations?: number;
}

export interface VerifyReport {
  ok: boolean;
  errors: string[];
  warnings: string[];
  seq: number;
  head: string;
  stateRoot: string | null;
  stats: {
    entries: number;
    users: number;
    tokens: number;
    deposits: number;
    withdrawals: number;
    checkpoints: number;
    anchored: number;
    liabilitiesZat: string;
    vaultZat: string | null;
  };
}

export async function verifyAll(entries: LogEntry[], checkpoints: Checkpoint[], opts: VerifyOptions = {}): Promise<VerifyReport> {
  const errors: string[] = [];
  const warnings: string[] = [];
  const chain = new Chain({ expectSequencer: opts.expectSequencer });
  const cpBySeq = new Map<number, Checkpoint>();
  for (const cp of checkpoints) cpBySeq.set(cp.seq, cp);
  const roots = new Map<number, string>();
  const onchain: { seq: number; ev: SystemEvent; address?: string; sendZat?: bigint }[] = [];
  let deposits = 0;
  let withdrawals = 0;

  for (const e of entries) {
    // Capture facts we need before the ledger mutates.
    let pre: { address?: string; sendZat?: bigint } = {};
    if (e?.kind === 'system' && chain.ledger) {
      const ev = e.data as SystemEvent;
      if (ev.type === 'deposit') pre.address = chain.ledger.users.get(ev.user)?.address ?? undefined;
      if (ev.type === 'withdraw_sent') {
        const w = chain.ledger.pending.get(ev.id);
        pre = { sendZat: w?.sendZat };
      }
    }
    try {
      chain.apply(e);
    } catch (err) {
      errors.push(err instanceof ChainError ? err.message : `entry ${e?.seq}: ${(err as Error).message}`);
      break; // everything after a broken link is unverifiable
    }
    if (e.kind === 'system') {
      const ev = e.data as SystemEvent;
      if (ev.type === 'deposit') deposits++;
      if (ev.type === 'withdraw_sent') withdrawals++;
      if (ev.type === 'deposit' || ev.type === 'withdraw_sent' || ev.type === 'expense') onchain.push({ seq: e.seq, ev, ...pre });
    }
    if (cpBySeq.has(e.seq)) roots.set(e.seq, chain.ledger!.stateRoot());
  }

  // Checkpoints: signed by the sequencer, linked to the log, state root matches the replay.
  let anchored = 0;
  for (const cp of checkpoints) {
    if (!chain.sequencer) break;
    if (!verifyCheckpointSig(cp, chain.sequencer)) {
      errors.push(`checkpoint ${cp.seq}: bad signature`);
      continue;
    }
    const e = entries[cp.seq];
    if (!e || e.hash !== cp.head) {
      errors.push(`checkpoint ${cp.seq}: head does not match the published log (history was rewritten)`);
      continue;
    }
    const r = roots.get(cp.seq);
    if (r !== undefined && r !== cp.stateRoot) errors.push(`checkpoint ${cp.seq}: state root does not match replay`);
    if (cp.anchor && opts.chain) {
      const a = await opts.chain.getAnchorMemo(cp.anchor.txid);
      const parsed = a ? parseAnchorMemo(a.memo) : null;
      if (!parsed) errors.push(`checkpoint ${cp.seq}: anchor tx ${cp.anchor.txid} not found or has no valid memo`);
      else if (parsed.seq !== cp.seq || parsed.head !== cp.head || parsed.stateRoot !== cp.stateRoot)
        errors.push(`checkpoint ${cp.seq}: anchored memo does not match checkpoint`);
      else {
        anchored++;
        if (a!.confirmations < 1) warnings.push(`checkpoint ${cp.seq}: its anchor transaction is not mined yet`);
      }
    }
  }
  if (opts.chain && checkpoints.length > 0 && anchored === 0) warnings.push('no checkpoint is anchored on Zcash yet');

  // Receipts held by users must match the log exactly. Only a genuinely signed receipt proves anything:
  // a made-up one must not be able to accuse an honest sequencer.
  for (const r of opts.receipts ?? []) {
    let genuine = false;
    try {
      genuine = !!chain.sequencer && entryHash(bodyOf(r)) === r.hash && verifyHex(r.sig, r.hash, chain.sequencer);
    } catch {
      genuine = false;
    }
    if (!genuine) {
      warnings.push(`receipt ${r?.seq}: not signed by this sequencer, ignored`);
      continue;
    }
    const e = entries[r.seq];
    if (!e) errors.push(`receipt ${r.seq}: entry missing from the published log (censored after acknowledging)`);
    else if (e.hash !== r.hash || canonicalize(e) !== canonicalize(r))
      errors.push(`receipt ${r.seq}: sequencer signed a different entry at this position (equivocation)`);
  }

  // On-chain facts.
  const minConf = opts.minConfirmations ?? 1;
  if (opts.chain) {
    for (const item of onchain) {
      const ev = item.ev;
      if (ev.type === 'deposit') {
        const d = await opts.chain.getDeposit(ev.ref, ev.txid, item.address);
        if (!d) errors.push(`entry ${item.seq}: deposit ${ev.ref} does not exist on-chain (fake credit)`);
        else {
          if (d.address !== item.address) errors.push(`entry ${item.seq}: deposit went to ${d.address}, not the user's address`);
          if (d.zat !== BigInt(ev.zat)) errors.push(`entry ${item.seq}: deposit amount on-chain ${d.zat} ≠ credited ${ev.zat}`);
          if (d.confirmations < minConf) warnings.push(`entry ${item.seq}: deposit has ${d.confirmations} confirmations`);
        }
      } else if (ev.type === 'withdraw_sent') {
        // The destination is sealed; the user can check it with their salt. Here: the tx exists and pays the right amount.
        const p = await opts.chain.getPayment(ev.txid);
        if (!p) errors.push(`entry ${item.seq}: withdrawal ${ev.id} marked sent but tx ${ev.txid} does not pay the user`);
        else {
          if (p.zat !== item.sendZat) errors.push(`entry ${item.seq}: withdrawal paid ${p.zat}, expected ${item.sendZat}`);
          if (p.fee !== BigInt(ev.networkFeeZat)) errors.push(`entry ${item.seq}: network fee on-chain ${p.fee} ≠ recorded ${ev.networkFeeZat}`);
        }
      } else if (ev.type === 'expense') {
        // Expenses are paid from the vault; the solvency check below catches any mismatch.
      }
    }
  }

  const ledger = chain.ledger;
  const liabilities = ledger ? ledger.liabilities() : 0n;
  let vault: bigint | null = null;
  if (opts.chain && ledger && errors.length === 0) {
    vault = await opts.chain.getVaultBalance();
    // Withdrawals whose payout is on the network but not mined have left the wallet balance while still
    // owed in the ledger; only those excuse a gap. A withdrawal never broadcast (or "parked") must still
    // be covered by the vault, or the operator could park one and take that much.
    let inFlight = 0n;
    for (const w of ledger.pending.values()) if (w.txid && (await opts.chain.getPayment(w.txid))) inFlight += w.zat;
    if (vault + inFlight < liabilities) errors.push(`INSOLVENT: vault holds ${vault} zat but owes ${liabilities} zat`);
    else if (vault < liabilities) warnings.push(`vault is ${liabilities - vault} zat below liabilities, covered by withdrawals in flight`);
    else if (vault > liabilities) warnings.push(`vault holds ${vault - liabilities} zat more than it owes (deposits not yet credited)`);
  }

  if (entries.length === 0) errors.push('empty log');
  return {
    ok: errors.length === 0,
    errors,
    warnings,
    seq: chain.seq,
    head: chain.head,
    stateRoot: ledger ? ledger.stateRoot() : null,
    stats: {
      entries: chain.seq + 1,
      users: ledger?.users.size ?? 0,
      tokens: ledger?.tokens.size ?? 0,
      deposits,
      withdrawals,
      checkpoints: checkpoints.length,
      anchored,
      liabilitiesZat: liabilities.toString(),
      vaultZat: vault === null ? null : vault.toString(),
    },
  };
}
